import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Package, MapPinned, Droplets, Medal, Check, Mail, Send } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POSITIONS = [
  {
    icon: Package,
    id: "packet-pickup",
    title: "Packet Pickup Shifts",
    hours: "Wednesday–Friday · Nov 18–20",
    desc: "Choose every shift you're available for. Pickup runs 10 AM – 4 PM at Hope Restored Missions in Plano.",
  },
  {
    icon: MapPinned,
    id: "venue",
    title: "Venue",
    hours: "Approx. 5 AM – 11:30 AM",
    desc: "Registration check-in, handing out runner food, assisting with contests and awards, and keeping the venue clean and organized.",
  },
  {
    icon: Droplets,
    id: "course",
    title: "Course Monitors & Water Stops",
    hours: "Approx. 6 AM – 11:30 AM",
    desc: "Great for groups and teams! Locations are assigned by the Volunteer Coordinator based on event needs and groups registered.",
  },
  {
    icon: Medal,
    id: "finish-line",
    title: "Finish Line Support",
    hours: "Approx. 5 AM – 11:30 AM",
    desc: "Set up finish-line items like water, medals, and trash boxes; hand out medals and water; and celebrate every runner's finish!",
  },
];

const PERKS = [
  "A meaningful morning giving back alongside our Allen neighbors",
  "An official 2Run4Hope volunteer t-shirt to wear with pride",
  "A joyful finish-line atmosphere with complimentary snacks and refreshments",
  "Documented community service hours for school, college, or employer programs",
  "Genuine connection with people who believe every mile means something",
];

const SHIFT_OPTIONS = [
  "Packet Pickup — Wed Nov 18, 10 AM–1 PM",
  "Packet Pickup — Wed Nov 18, 1–4 PM",
  "Packet Pickup — Thu Nov 19, 10 AM–1 PM",
  "Packet Pickup — Thu Nov 19, 1–4 PM",
  "Packet Pickup — Fri Nov 20, 10 AM–1 PM",
  "Packet Pickup — Fri Nov 20, 1–4 PM",
  "Venue Crew — Race Day, 5 AM–11:30 AM",
  "Course Monitor / Water Stop — Race Day, 6 AM–11:30 AM",
  "Finish Line Support — Race Day, 5 AM–11:30 AM",
];

export const Volunteers = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [positions, setPositions] = useState([]);
  const [sending, setSending] = useState(false);

  const toggle = (p) =>
    setPositions((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));

  const submit = async (e) => {
    e.preventDefault();
    if (!positions.length) {
      toast.error("Please choose at least one position or shift.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/volunteers`, { ...form, positions });
      toast.success("Thanks for volunteering! Daphne will be in touch with your assignment.");
      setForm({ name: "", email: "", phone: "", notes: "" });
      setPositions([]);
    } catch {
      toast.error("Something went wrong — please try again or email Daphne directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="volunteers" data-testid="volunteers-section" className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="mb-14 grid gap-6 lg:grid-cols-2 lg:items-end">
          <Reveal>
            <div>
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-hope-green">
                Positions Available
              </p>
              <h2
                data-testid="volunteers-heading"
                className="font-heading text-4xl font-black leading-[1.05] tracking-tighter text-hope-ink sm:text-5xl lg:text-6xl"
              >
                Volunteers
                <br />
                Needed<span className="text-hope-red">.</span>
              </h2>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="max-w-md text-base leading-relaxed text-zinc-600 md:text-lg lg:ml-auto">
              Not running? You can still move the mission. Pick a role below —
              every volunteer makes race morning possible.
            </p>
          </Reveal>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4" data-testid="volunteer-positions-grid">
          {POSITIONS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1}>
              <div
                data-testid={`volunteer-position-${p.id}`}
                className="group flex h-full flex-col rounded-3xl border border-black/5 bg-hope-cream p-7 transition-all duration-300 hover:-translate-y-2 hover:border-hope-green/40 hover:shadow-md"
              >
                <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-hope-green text-white transition-colors duration-300 group-hover:bg-hope-red">
                  <p.icon className="h-6 w-6" />
                </span>
                <h3 className="font-heading text-lg font-bold leading-tight text-hope-ink">{p.title}</h3>
                <p className="mt-2 text-xs font-bold uppercase tracking-[0.12em] text-hope-green">{p.hours}</p>
                <p className="mt-3 text-sm leading-relaxed text-zinc-600">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div data-testid="volunteer-perks" className="mt-12 rounded-3xl bg-[#062012] p-8 text-white md:p-10">
            <h3 className="font-heading text-2xl font-bold">Volunteer Perks</h3>
            <ul className="mt-6 grid gap-x-10 gap-y-4 sm:grid-cols-2">
              {PERKS.map((perk) => (
                <li key={perk} className="flex items-start gap-3 text-sm leading-relaxed text-white/85">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-hope-bright" />
                  {perk}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        <div className="mt-12 grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form
                onSubmit={submit}
                data-testid="volunteer-form"
                className="rounded-3xl border border-black/5 bg-hope-cream p-7 md:p-9"
              >
                <h3 className="font-heading text-2xl font-bold text-hope-ink">Sign Up to Volunteer</h3>
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  <input
                    data-testid="volunteer-name-input"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Full name"
                    className="rounded-2xl border border-black/10 bg-white px-5 py-3.5 text-sm text-hope-ink placeholder-zinc-400 outline-none transition-colors duration-200 focus:border-hope-green"
                  />
                  <input
                    data-testid="volunteer-email-input"
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Email"
                    className="rounded-2xl border border-black/10 bg-white px-5 py-3.5 text-sm text-hope-ink placeholder-zinc-400 outline-none transition-colors duration-200 focus:border-hope-green"
                  />
                  <input
                    data-testid="volunteer-phone-input"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="Phone (optional)"
                    className="rounded-2xl border border-black/10 bg-white px-5 py-3.5 text-sm text-hope-ink placeholder-zinc-400 outline-none transition-colors duration-200 focus:border-hope-green sm:col-span-2"
                  />
                </div>
                <p className="mt-6 text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">
                  Choose all shifts &amp; positions you're available for
                </p>
                <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
                  {SHIFT_OPTIONS.map((opt) => (
                    <label
                      key={opt}
                      data-testid={`volunteer-shift-${opt.slice(0, 24).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 text-sm transition-all duration-200 ${
                        positions.includes(opt)
                          ? "border-hope-green bg-white font-semibold text-hope-ink shadow-sm"
                          : "border-black/10 bg-white/60 text-zinc-600 hover:border-hope-green/50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={positions.includes(opt)}
                        onChange={() => toggle(opt)}
                        className="mt-0.5 h-4 w-4 shrink-0 accent-[#1E9E4A]"
                      />
                      {opt}
                    </label>
                  ))}
                </div>
                <button
                  data-testid="volunteer-submit-btn"
                  type="submit"
                  disabled={sending}
                  className="group mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-hope-green px-8 py-4 font-heading text-base font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-hope-ink disabled:opacity-50"
                >
                  {sending ? "Sending…" : "Sign Me Up"}
                  <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                </button>
              </form>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.2}>
              <div className="rounded-3xl border-2 border-dashed border-hope-green/40 p-7 md:p-9">
                <h3 className="font-heading text-xl font-bold text-hope-ink">Questions About Volunteering?</h3>
                <p className="mt-3 text-sm leading-relaxed text-zinc-600">
                  Course locations are assigned based on event needs and
                  registered groups. Questions? Reach out anytime:
                </p>
                <a
                  href="mailto:daphne.warren@hoperestoredmissions.org"
                  data-testid="volunteer-contact-link"
                  className="mt-5 inline-flex items-center gap-3 rounded-2xl bg-white px-5 py-4 text-sm font-semibold text-hope-ink shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
                >
                  <Mail className="h-5 w-5 text-hope-green" />
                  <span>
                    Daphne Warren
                    <span className="block text-xs font-semibold uppercase tracking-[0.1em] text-hope-green">
                      Director of Administration
                    </span>
                    <span className="block text-xs font-medium text-zinc-500">
                      daphne.warren@hoperestoredmissions.org
                    </span>
                  </span>
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
